import {Fragment, useEffect, useRef, useState} from 'react'
import {useTranslation} from 'next-i18next'
import {makeStyles} from '@material-ui/core/styles'
import AppTypography from 'src/components/elements/typography/AppTypography'
import LocationMap from 'src/components/elements/locationMap/LocationMap'
import Section from './Section'
import {PropertyType} from 'src/types/property'
import {usePropertyAddress} from 'src/hooks/property'
import {globalZIndex} from 'src/utils/styles'
import {Marker as LeafletMarker, Map as LeafletMap} from 'leaflet'
import {useLeafletInstance} from 'src/hooks/dom'
import {publicAddDataCollectionApi} from 'src/services/api/dataCollection'

const MAP_HEIGHT = 324
const mapContainerStyle: React.CSSProperties = {
  height: MAP_HEIGHT,
  borderRadius: 16,
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
  marginBottom: 32,
}

//todo convert to styles.module.css or tailwind utility classes
const useStyles = makeStyles(({spacing}) => ({
  map: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: '100%',
    borderRadius: 16,
    marginBottom: spacing(4),
    cursor: 'pointer',
    overflow: 'hidden',
    height: MAP_HEIGHT,
    '& p': {
      zIndex: globalZIndex.aboveBase,
    },
    '& p:nth-child(2)': {
      textDecoration: 'underline',
    },
    '& img': {
      borderRadius: 16,
      objectFit: 'cover',
    },
  },
}))

export default function Location(props: {property: PropertyType}) {
  const {property} = props

  const {t} = useTranslation('common')
  const {latitude, longitude} = property
  const [openMap, setOpenMap] = useState<boolean>(false)

  const propertyAddress = usePropertyAddress(property)

  const onClickMap = () => {
    setOpenMap(true)
    publicAddDataCollectionApi('see_location_map_unique_property_page', {
      property_id: property.id,
      property_uid: property.uid,
    })
  }

  return (
    <Section
      title={t('location')}
      subTitle={
        <>
          <AppTypography variant="body">{propertyAddress}</AppTypography>
          <AppTypography variant="body" neutralColor={500}>
            {t('address_will_be_provided')}
          </AppTypography>
        </>
      }
    >
      <Fragment>
        {openMap ? (
          <GoogleMap latitude={latitude} longitude={longitude} />
        ) : (
          <ClickToOpenMap onClick={onClickMap} />
        )}
      </Fragment>
    </Section>
  )
}

function ClickToOpenMap(props: {onClick: () => void}) {
  const {onClick} = props
  const classes = useStyles()
  const {t} = useTranslation('common')

  return (
    <div className={classes.map} onClick={onClick}>
      <div className="absolute w-full flex flex-col justify-center items-center h-[324px]">
        <AppTypography variant="h5" htmlColor="#FFFFFF" className="text-center">
          {t('To view property location on map,')}
        </AppTypography>
        <AppTypography variant="h5" htmlColor="#FFFFFF" className="text-center">
          {t('click here')}
        </AppTypography>
      </div>
      <img
        className="w-full h-full object-cover object-left-center"
        src="/images/google_map.png"
        alt="Google map"
      />
    </div>
  )
}

function GoogleMap(props: {latitude: number; longitude: number}) {
  const {latitude, longitude} = props

  const leafletInstance = useLeafletInstance()
  const mapRef = useRef<null | LeafletMap>(null)
  const markerRef = useRef<null | LeafletMarker>(null)

  const onMapLoaded = async (map: LeafletMap) => {
    mapRef.current = map

    let icon = leafletInstance.icon({
      iconUrl: '/images/map/area.svg',
      iconSize: [48, 48],
      iconAnchor: [48 / 2, 48 / 2],
    })

    markerRef.current = leafletInstance
      .marker(
        {lat: latitude, lng: longitude},
        {
          icon: icon,
        },
      )
      .addTo(mapRef.current)
  }

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.setView({lat: latitude, lng: longitude})
    }
  }, [latitude, longitude])

  //still render a div to keep the height and not have a flash of content
  if (!leafletInstance) {
    return (
      <div
        className="w-full rounded-2xl overflow-hidden"
        style={{height: MAP_HEIGHT}}
      >
        <img
          className="w-full h-full object-cover object-left-center"
          src="/images/google_map.png"
          alt="Google map"
        />
      </div>
    )
  }

  return (
    <div className="w-full" style={{height: MAP_HEIGHT}}>
      <LocationMap
        id="map"
        mapContainerStyle={mapContainerStyle}
        initialZoom={10}
        initialCenter={{lat: latitude, lng: longitude}}
        options={{
          minZoom: 5,
          maxZoom: 14,
          dragging: true,
        }}
        onMapLoaded={onMapLoaded}
      />
    </div>
  )
}
